import { Field, FieldOptionBase } from 'module/api/field/types';
import {
  FilterableField,
  MultiSelectField,
  SingleSelectField,
} from 'module/types/filter-field/field/filterable-field';
import {
  toRowNodeComparator,
  Comparator,
} from 'module/ui/components/atoms/grid/plugins/with-columns';
import { ColDef } from 'ag-grid-community';
import { memoize } from 'lodash';
import { getBaseComparator } from 'utils/comparators/base-comparators';
import { sortByText } from 'utils/sort-by-text';

type OptionsField = SingleSelectField | MultiSelectField;
type Nullable<T> = T | null;

type NewField = FilterableField &
  Pick<
    Field,
    | 'fieldDataType'
    | 'fieldType'
    | 'optionsAvailable'
    | 'isEnabled'
    | 'isEditable'
  >;

const getProjectComparator = <T = any>(
  field: Pick<NewField, 'metaData' | 'fieldName' | 'fieldTypeDisplay' | 'type'>,
): Comparator<T> | undefined => {
  const options =
    (field as OptionsField).metaData?.options ?? ([] as FieldOptionBase[]);
  const getLabel = memoize(
    (value: Nullable<number>) =>
      value == null ? null : options.find((o) => o.value === value)?.label,
    (value) => value?.toString() ?? 'null',
  );

  if (field.fieldName === 'currency') {
    const getValue = (record) => record?.currencyCode ?? ''; // TODO: check with PM if sorting based on code or name

    return (a: T, b: T) => {
      const a_value = getValue(a as any);
      const b_value = getValue(b as any);

      return sortByText(a_value, b_value);
    };
  }

  if (field.fieldName === 'currentPhases') {
    const getLengthComparator = (a, b) => {
      if (a.length === b.length) {
        return 0;
      }

      return a.length > b.length ? 1 : -1;
    };

    const compareByArrayValue = (a, b) => {
      const aVal = a.join(',');
      const bVal = b.join(',');

      if (aVal === bVal) {
        return 0;
      }

      return aVal > bVal ? 1 : -1;
    };

    const getVal = memoize(
      (value: Nullable<number[]>): string[] =>
        (value ?? []).map((x) => getLabel(x)).filter((x) => x) as string[],
      (value: Nullable<number[]>) =>
        Array.from(new Set(value ?? []))
          .map((x) => x.toString())
          .join(','),
    );

    return (first: T, sec: T) => {
      const a = getVal(first as any) ?? 'null';
      const b = getVal(sec as any) ?? 'null';

      if (a.length !== b.length) {
        return getLengthComparator(a, b);
      }

      return compareByArrayValue(a, b);
    };
  }

  return getBaseComparator(field);
};

export const getComparator = (field: FilterableField): ColDef['comparator'] => {
  return toRowNodeComparator(getProjectComparator(field));
};
